import { z } from "zod";

/**
 * Profit Center: Deckungsbeitrag je Bestellung, Artikel und Kanal.
 * Umsatz netto (ohne Steuer, nach Rabatten, abzüglich Erstattungen) − Wareneinsatz (Einstandspreis × Menge)
 * − Kanal-/Zahlungsgebühren − Fulfillment-Kosten (nur mit Freischaltung) = Deckungsbeitrag.
 * Alle Beträge in Cent (Minor Units); Einstandspreise netto in EUR je Stück (4 Nachkommastellen).
 */

export const channelFeeSchema = z.object({
  /** Prozent vom Bruttoumsatz (Zahlungsanbieter, Marktplatzprovision) */
  percent: z.number().min(0).max(50).default(0),
  /** fester Betrag je Bestellung in EUR */
  fixedEur: z.number().min(0).max(100).default(0),
});
export type ChannelFee = z.infer<typeof channelFeeSchema>;

export const profitSettingsSchema = z.object({
  /** Steuersatz für Bestellungen ohne Steuerangabe aus dem Kanal (Prozent) */
  vatRatePct: z.number().min(0).max(30).default(19),
  /** Gebühren je Kanal (channelId → Gebühr) */
  channelFees: z.record(z.string(), channelFeeSchema).default({}),
  /** Versanderlös als Umsatz zählen */
  includeShippingRevenue: z.boolean().default(true),
});
export type ProfitSettings = z.infer<typeof profitSettingsSchema>;

/** Gleitender Durchschnitt: Bestand vor dem Eingang zum alten Ø, Eingangsmenge zum Einstandspreis */
export function movingAverageCost(p: { avg: number | null; stockBefore: number; qty: number; unitCost: number }): number {
  const stock = Math.max(0, p.stockBefore);
  if (p.qty <= 0) return p.avg ?? p.unitCost;
  if (p.avg == null || stock === 0) return round4(p.unitCost);
  return round4((p.avg * stock + p.unitCost * p.qty) / (stock + p.qty));
}

export const round4 = (x: number) => Math.round(x * 10_000) / 10_000;
/** Netto aus Brutto bei Steuersatz (Cent) */
export const netOfGross = (grossMinor: number, vatPct: number) => Math.round(grossMinor / (1 + vatPct / 100));

/** Umsatzdaten einer Bestellung (aus dem Kanal oder abgeleitet) */
export interface OrderRevenue {
  grossMinor: number;
  taxMinor: number;
  shippingGrossMinor: number;
  refundedGrossMinor: number;
  /** Netto-Umsatz je Position (nach Rabatten) */
  lines: { sku: string; quantity: number; netMinor: number }[];
  /** true = Werte aus dem Kanal; false = aus Auftragssumme mit Standard-Steuersatz geschätzt */
  exact: boolean;
}

export interface ContributionInput {
  revenue: OrderRevenue;
  /** Wareneinsatz je Position: Menge × Einstandspreis (null = Preis fehlt) */
  costs: { sku: string; quantity: number; unitCost: number | null }[];
  fee: ChannelFee | undefined;
  /** Fulfillment-Kosten je Bestellung in Cent (null = nicht freigeschaltet/unbekannt) */
  fulfillmentMinor: number | null;
  /** von Hand erfasste Kosten zu dieser Bestellung (Cent, netto) */
  otherMinor?: number;
  includeShipping: boolean;
}

export interface Contribution {
  revenueNetMinor: number;
  shippingNetMinor: number;
  refundsNetMinor: number;
  cogsMinor: number;
  feesMinor: number;
  fulfillmentMinor: number;
  /** manuelle Kosten zur Bestellung */
  otherMinor: number;
  contributionMinor: number;
  /** Deckungsbeitrag in % vom Netto-Umsatz (null bei 0 Umsatz) */
  marginPct: number | null;
  /** Positionen ohne Einstandspreis (Wareneinsatz unvollständig) */
  missingCostSkus: string[];
}

export function orderContribution(i: ContributionInput): Contribution {
  const r = i.revenue;
  const netTotal = r.grossMinor - r.taxMinor;
  // Steueranteil gleichmäßig auf Versand und Erstattung umlegen (Kanäle liefern Steuer nur je Bestellung zuverlässig)
  const netRatio = r.grossMinor > 0 ? netTotal / r.grossMinor : 1;
  const shippingNet = Math.round(r.shippingGrossMinor * netRatio);
  const refundsNet = Math.round(Math.min(r.refundedGrossMinor, r.grossMinor) * netRatio);
  const revenueNet = netTotal - (i.includeShipping ? 0 : shippingNet) - refundsNet;
  const missing = new Set<string>();
  let cogs = 0;
  for (const c of i.costs) {
    if (c.unitCost == null) missing.add(c.sku);
    else cogs += Math.round(c.unitCost * 100 * c.quantity);
  }
  // Gebühren auf den tatsächlich bezahlten Bruttobetrag (Erstattungen bekommt man je nach Anbieter nicht zurück → voll)
  const fees = i.fee ? Math.round((r.grossMinor * i.fee.percent) / 100 + i.fee.fixedEur * 100) : 0;
  const fulfillment = i.fulfillmentMinor ?? 0;
  const other = i.otherMinor ?? 0;
  const contribution = revenueNet - cogs - fees - fulfillment - other;
  return {
    revenueNetMinor: revenueNet,
    shippingNetMinor: shippingNet,
    refundsNetMinor: refundsNet,
    cogsMinor: cogs,
    feesMinor: fees,
    fulfillmentMinor: fulfillment,
    otherMinor: other,
    contributionMinor: contribution,
    marginPct: revenueNet > 0 ? Math.round((contribution / revenueNet) * 1000) / 10 : null,
    missingCostSkus: [...missing],
  };
}

/** Umsatz ohne Kanaldaten: Auftragssumme brutto, Steuer mit Standard-Steuersatz, Positionen anteilig nach Listenpreis/Menge */
export function estimatedRevenue(p: { totalMinor: number; vatRatePct: number; lines: { sku: string; quantity: number; unitPriceMinor: number | null }[] }): OrderRevenue {
  const gross = Math.max(0, p.totalMinor);
  const net = netOfGross(gross, p.vatRatePct);
  const weights = p.lines.map((l) => (l.unitPriceMinor ?? 0) * l.quantity);
  const sum = weights.reduce((s, w) => s + w, 0);
  const units = p.lines.reduce((s, l) => s + l.quantity, 0);
  return {
    grossMinor: gross,
    taxMinor: gross - net,
    shippingGrossMinor: 0,
    refundedGrossMinor: 0,
    lines: p.lines.map((l, idx) => ({ sku: l.sku, quantity: l.quantity, netMinor: Math.round(sum > 0 ? (net * weights[idx]!) / sum : units > 0 ? (net * l.quantity) / units : 0) })),
    exact: false,
  };
}

// ── Manuelle Kosten ─────────────────────────────────────────────────

export const PROFIT_COST_CATEGORIES = ["Marketing", "Software", "Personal", "Miete", "Verpackung", "Versand", "Gebühren", "Sonstiges"] as const;
const month = z.string().regex(/^\d{4}-\d{2}$/, "Monat als JJJJ-MM");

/**
 * Von Hand erfasste Kosten: monatliche Fixkosten (ab/bis Monat, im Bericht tagesgenau auf den Zeitraum umgelegt)
 * oder Kosten zu einer Bestellung (mindern deren Deckungsbeitrag). Beträge netto in EUR.
 */
export const profitCostInputSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("monthly"),
    label: z.string().trim().min(2, "Bezeichnung fehlt").max(120),
    category: z.string().trim().max(40).default("Sonstiges"),
    amountEur: z.number().positive("Betrag fehlt").max(10_000_000),
    startMonth: month,
    endMonth: month.nullable().default(null),
    note: z.string().trim().max(500).default(""),
  }).refine((c) => !c.endMonth || c.endMonth >= c.startMonth, { message: "Ende liegt vor dem Beginn", path: ["endMonth"] }),
  z.object({
    kind: z.literal("order"),
    label: z.string().trim().min(2, "Bezeichnung fehlt").max(120),
    category: z.string().trim().max(40).default("Sonstiges"),
    amountEur: z.number().positive("Betrag fehlt").max(1_000_000),
    /** Auftrag per ID oder Auftragsnummer */
    orderId: z.string().uuid().optional(),
    orderNumber: z.string().trim().min(1).max(60).optional(),
    note: z.string().trim().max(500).default(""),
  }).refine((c) => !!c.orderId || !!c.orderNumber, { message: "Auftrag fehlt", path: ["orderNumber"] }),
]);
export type ProfitCostInput = z.infer<typeof profitCostInputSchema>;

/**
 * Anteil monatlicher Fixkosten am Zeitraum [from, to] (Kalendertage, inklusive): je Tag Monatsbetrag / Tage des Monats,
 * nur in Monaten zwischen Beginn und Ende. Ergebnis in Cent.
 */
export function fixedCostShareMinor(c: { amountMinor: number; startMonth: string; endMonth: string | null }, from: string, to: string): number {
  let sum = 0;
  const end = Date.parse(`${to}T00:00:00Z`);
  for (let t = Date.parse(`${from}T00:00:00Z`); t <= end; t += 86_400_000) {
    const day = new Date(t);
    const m = day.toISOString().slice(0, 7);
    if (m < c.startMonth || (c.endMonth && m > c.endMonth)) continue;
    const daysInMonth = new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth() + 1, 0)).getUTCDate();
    sum += c.amountMinor / daysInMonth;
  }
  return Math.round(sum);
}
